import { Call, Device } from "@twilio/voice-sdk";
import type { useCompassusPatientCommunication } from "../CompassusPatient/Communication/useCompassusPatientCommunication";

type CommunicationApi = ReturnType<typeof useCompassusPatientCommunication>;

export type PatientCallPhase =
  | "idle"
  | "preparing"
  | "ringing"
  | "connected"
  | "reconnecting"
  | "ended"
  | "canceled"
  | "busy"
  | "rejected"
  | "error";

export const ACTIVE_CALL_PHASES: PatientCallPhase[] = [
  "preparing",
  "ringing",
  "connected",
  "reconnecting",
];

export const PATIENT_CALL_PHASE_LABELS: Record<PatientCallPhase, string> = {
  idle: "Ready",
  preparing: "Preparing call…",
  ringing: "Ringing…",
  connected: "Connected",
  reconnecting: "Reconnecting…",
  ended: "Call ended",
  canceled: "Call canceled",
  busy: "Line busy",
  rejected: "Call not answered",
  error: "Call failed",
};

export const phaseFromCallLogStatus = (
  status: string
): PatientCallPhase | null => {
  const normalized = status
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-");
  if (
    ["queued", "initiated", "ringing", "dialing", "connecting"].includes(
      normalized
    )
  )
    return "ringing";
  if (["in-progress", "answered", "connected"].includes(normalized))
    return "connected";
  if (["completed"].includes(normalized)) return "ended";
  if (normalized === "no-answer") return "rejected";
  if (normalized === "busy") return "busy";
  if (["canceled", "cancelled"].includes(normalized)) return "canceled";
  if (["failed", "error"].includes(normalized)) return "error";
  return null;
};

export const formatCallDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(
    remainingSeconds
  ).padStart(2, "0")}`;
};

export interface PatientCallDeps {
  getVoiceToken: CommunicationApi["getVoiceToken"];
  startCallLog: CommunicationApi["startCallLog"];
  mutateCalls: () => Promise<unknown>;
  /** Direct (non-SWR) call-log fetch so status polling keeps working after
   *  the component that started the call has unmounted. */
  fetchCallLogs?: () => Promise<ICompassusPatientCallLog[] | null | undefined>;
}

export interface PatientCallRoute {
  clinicId: number;
  patientId: number;
}

export interface PatientCallContext {
  /** Identifies which hook consumer owns the current call. */
  ownerKey: string;
  phoneNumber: string;
  displayName: string;
  /** Persistent calls survive unmount of the view that started them and are
   *  surfaced through the global active-call pill. */
  persist: boolean;
  /** Navigation target for the pill's "go to call" action. */
  route: PatientCallRoute | null;
}

export interface PatientCallSnapshot {
  phase: PatientCallPhase;
  elapsedSeconds: number;
  volume: { input: number; output: number };
  errorMessage: string | null;
  isMuted: boolean;
  wasConnected: boolean;
  activeCallLogId: number | null;
  context: PatientCallContext | null;
  /** Owner keys of currently mounted full dialer views; the pill hides when
   *  the active call's own dialer is already on screen. */
  visibleDialerOwnerKeys: string[];
}

export interface StartPatientCallOptions {
  phoneNumber: string;
  ownerKey: string;
  displayName: string;
  callLabel: string;
  linkNumberOnCall: boolean;
  persist: boolean;
  route: PatientCallRoute | null;
  deps: PatientCallDeps;
}

const INITIAL_SNAPSHOT: PatientCallSnapshot = {
  phase: "idle",
  elapsedSeconds: 0,
  volume: { input: 0, output: 0 },
  errorMessage: null,
  isMuted: false,
  wasConnected: false,
  activeCallLogId: null,
  context: null,
  visibleDialerOwnerKeys: [],
};

const STATUS_POLL_INTERVAL_MS = 1500;
const DISCONNECT_FALLBACK_MS = 5000;
const GATEWAY_STATUS_GRACE_MS = 5000;
const DEFERRED_GATEWAY_ERROR_CODES = new Set([31000, 31005]);

export const twilioSdkErrorCode = (error: unknown): number | null => {
  if (!error || typeof error !== "object") return null;
  const candidate = error as {
    code?: unknown;
    twilioError?: { code?: unknown };
  };
  const value = candidate.twilioError?.code ?? candidate.code;
  return typeof value === "number" ? value : Number(value) || null;
};

export const shouldAwaitBackendCallStatus = ({
  error,
  phase,
  wasConnected,
  activeCallLogId,
}: {
  error: unknown;
  phase: PatientCallPhase;
  wasConnected: boolean;
  activeCallLogId: number | null;
}): boolean =>
  Boolean(
    activeCallLogId &&
      !wasConnected &&
      (phase === "preparing" || phase === "ringing") &&
      DEFERRED_GATEWAY_ERROR_CODES.has(twilioSdkErrorCode(error) ?? -1)
  );

/**
 * Module-level owner of the Twilio voice call so an in-progress patient call
 * survives tab switches, drawer closes, and route navigation. Components
 * consume it through `useTwilioPatientCall` (scoped view) or the global
 * `CompassusActiveCallPill` overlay.
 */
class PatientCallManager {
  private listeners = new Set<() => void>();
  private snapshot: PatientCallSnapshot = INITIAL_SNAPSHOT;
  private device: Device | null = null;
  private activeCall: Call | null = null;
  private deps: PatientCallDeps | null = null;
  private durationTimer: ReturnType<typeof setInterval> | null = null;
  private statusPoll: ReturnType<typeof setInterval> | null = null;
  private statusRefreshInFlight = false;
  private statusPollGeneration = 0;
  private disconnectFallback: ReturnType<typeof setTimeout> | null = null;
  private acceptedAt: number | null = null;
  private terminalHandled = false;
  private suppressEvents = false;
  private wasConnectedInternal = false;
  private visibleDialers = new Map<string, number>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): PatientCallSnapshot => this.snapshot;

  private emit(patch: Partial<PatientCallSnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  /** Marks a full dialer view (keypad + live call card) as on screen. */
  registerVisibleDialer(ownerKey: string): () => void {
    this.visibleDialers.set(
      ownerKey,
      (this.visibleDialers.get(ownerKey) ?? 0) + 1
    );
    this.emitVisibleDialers();
    return () => {
      const count = (this.visibleDialers.get(ownerKey) ?? 0) - 1;
      if (count <= 0) this.visibleDialers.delete(ownerKey);
      else this.visibleDialers.set(ownerKey, count);
      this.emitVisibleDialers();
    };
  }

  private emitVisibleDialers() {
    this.emit({ visibleDialerOwnerKeys: [...this.visibleDialers.keys()] });
  }

  private stopStatusPolling() {
    this.statusPollGeneration += 1;
    this.statusRefreshInFlight = false;
    if (this.statusPoll) {
      clearInterval(this.statusPoll);
      this.statusPoll = null;
    }
    if (this.disconnectFallback) {
      clearTimeout(this.disconnectFallback);
      this.disconnectFallback = null;
    }
  }

  private async mutateCallsSafely(deps = this.deps): Promise<void> {
    try {
      await deps?.mutateCalls();
    } catch {
      // Call-history refresh failures must not interrupt the voice lifecycle.
    }
  }

  private async refreshCallStatus(generation: number): Promise<void> {
    if (this.statusRefreshInFlight) return;
    const deps = this.deps;
    if (!deps || generation !== this.statusPollGeneration) return;

    this.statusRefreshInFlight = true;
    try {
      const [callLogsResult] = await Promise.allSettled([
        deps.fetchCallLogs?.() ?? Promise.resolve(null),
        deps.mutateCalls(),
      ]);
      if (
        generation === this.statusPollGeneration &&
        callLogsResult.status === "fulfilled" &&
        callLogsResult.value
      ) {
        this.applyCallLogs(callLogsResult.value);
      }
    } catch {
      // Defensive: applying refreshed logs must not reject the polling task.
    } finally {
      if (generation === this.statusPollGeneration) {
        this.statusRefreshInFlight = false;
      }
    }
  }

  private stopTimer() {
    if (this.durationTimer) {
      clearInterval(this.durationTimer);
      this.durationTimer = null;
    }
    this.acceptedAt = null;
  }

  private startTimer() {
    this.stopTimer();
    this.acceptedAt = Date.now();
    this.wasConnectedInternal = true;
    this.emit({ wasConnected: true, elapsedSeconds: 0 });
    this.durationTimer = setInterval(() => {
      if (!this.acceptedAt) return;
      this.emit({
        elapsedSeconds: Math.floor((Date.now() - this.acceptedAt) / 1000),
      });
    }, 1000);
  }

  private startStatusPolling() {
    this.stopStatusPolling();
    const generation = this.statusPollGeneration;
    this.statusPoll = setInterval(() => {
      void this.refreshCallStatus(generation);
    }, STATUS_POLL_INTERVAL_MS);
  }

  private async finishCall(nextPhase: PatientCallPhase, message?: string) {
    if (this.terminalHandled || this.suppressEvents) return;
    this.terminalHandled = true;
    this.stopStatusPolling();
    this.stopTimer();
    this.emit({
      phase: nextPhase,
      errorMessage: message ?? null,
      volume: { input: 0, output: 0 },
      activeCallLogId: null,
    });
    this.activeCall = null;
    this.device?.destroy();
    this.device = null;
    await this.mutateCallsSafely();
  }

  private handleSdkError(error: unknown) {
    const message =
      error && typeof error === "object" && "message" in error
        ? String((error as { message?: unknown }).message || "Call failed.")
        : "Call failed.";
    if (
      !shouldAwaitBackendCallStatus({
        error,
        phase: this.snapshot.phase,
        wasConnected: this.wasConnectedInternal,
        activeCallLogId: this.snapshot.activeCallLogId,
      })
    ) {
      void this.finishCall("error", message);
      return;
    }

    // A busy/no-answer PSTN leg can cause Twilio's gateway to close the
    // browser signaling leg with 31000/31005 before its status callback reaches
    // our backend. Keep the call-log identity and polling alive long enough for
    // the authoritative backend status to replace the generic SDK error.
    this.emit({ phase: "ringing", errorMessage: null });
    void this.refreshCallStatus(this.statusPollGeneration);
    if (this.disconnectFallback) clearTimeout(this.disconnectFallback);
    this.disconnectFallback = setTimeout(() => {
      void this.finishCall("error", "Connection lost while placing the call.");
    }, GATEWAY_STATUS_GRACE_MS);
  }

  /** Tears down the Twilio device; optionally resets the visible phase too. */
  destroy(resetPhase: boolean) {
    this.suppressEvents = true;
    this.stopStatusPolling();
    this.stopTimer();
    this.activeCall?.disconnect();
    this.device?.disconnectAll();
    this.device?.destroy();
    this.activeCall = null;
    this.device = null;
    this.emit({
      volume: { input: 0, output: 0 },
      isMuted: false,
      activeCallLogId: null,
      ...(resetPhase
        ? {
            phase: "idle" as PatientCallPhase,
            elapsedSeconds: 0,
            errorMessage: null,
            wasConnected: false,
            context: null,
          }
        : {}),
    });
    if (resetPhase) this.wasConnectedInternal = false;
    queueMicrotask(() => {
      this.suppressEvents = false;
    });
  }

  /** destroy(), but only when `ownerKey` owns the current call. */
  destroyIfOwner(ownerKey: string, resetPhase: boolean) {
    const context = this.snapshot.context;
    if (context && context.ownerKey !== ownerKey) return;
    this.destroy(resetPhase);
  }

  applyCallLogs(callLogs: ICompassusPatientCallLog[]) {
    const activeCallLogId = this.snapshot.activeCallLogId;
    if (!activeCallLogId) return;
    const activeLog = callLogs.find((item) => item.id === activeCallLogId);
    if (activeLog) this.applyCallLogStatus(activeLog);
  }

  private applyCallLogStatus(callLog: ICompassusPatientCallLog) {
    const backendPhase = phaseFromCallLogStatus(callLog.status);
    if (!backendPhase) return;

    if (backendPhase === "connected") {
      if (!this.wasConnectedInternal) this.startTimer();
      this.emit({ phase: "connected" });
      return;
    }

    if (backendPhase === "ringing") {
      if (!this.wasConnectedInternal) this.emit({ phase: "ringing" });
      return;
    }

    if (callLog.duration_seconds != null) {
      const patch: Partial<PatientCallSnapshot> = {
        elapsedSeconds: callLog.duration_seconds,
      };
      if (callLog.duration_seconds > 0) {
        this.wasConnectedInternal = true;
        patch.wasConnected = true;
      }
      this.emit(patch);
    }
    void this.finishCall(backendPhase, callLog.error_message ?? undefined);
  }

  async startCall(options: StartPatientCallOptions): Promise<boolean> {
    if (ACTIVE_CALL_PHASES.includes(this.snapshot.phase)) return false;

    this.destroy(false);
    this.terminalHandled = false;
    this.wasConnectedInternal = false;
    this.deps = options.deps;
    this.emit({
      phase: "preparing",
      elapsedSeconds: 0,
      volume: { input: 0, output: 0 },
      errorMessage: null,
      isMuted: false,
      wasConnected: false,
      activeCallLogId: null,
      context: {
        ownerKey: options.ownerKey,
        phoneNumber: options.phoneNumber,
        displayName: options.displayName,
        persist: options.persist,
        route: options.route,
      },
    });

    try {
      const callLogResponse = await options.deps.startCallLog({
        phone_number: options.phoneNumber,
        label: options.callLabel,
        link_number: options.linkNumberOnCall,
      });
      if (!callLogResponse.success) {
        const message =
          callLogResponse.error?.message ?? "Could not start call.";
        this.emit({ errorMessage: message, phase: "error" });
        return false;
      }
      this.emit({ activeCallLogId: callLogResponse.data.call.id });
      this.startStatusPolling();

      const tokenResponse = await options.deps.getVoiceToken();
      if (!tokenResponse.success) {
        const message =
          tokenResponse.error?.message ?? "Could not get call token.";
        this.emit({ errorMessage: message, phase: "error" });
        return false;
      }

      const device = new Device(tokenResponse.data.token, {
        logLevel: "warn",
      });
      this.device = device;
      device.on("error", (error) => {
        this.handleSdkError(error);
      });

      const call = await device.connect({
        params: {
          To: callLogResponse.data.phone_number,
          CallLogId: String(callLogResponse.data.call.id),
        },
      });
      this.activeCall = call;
      this.emit({ phase: "ringing" });

      call.on("ringing", () => this.emit({ phase: "ringing" }));
      call.on("accept", () => {
        // For outbound PSTN, SDK acceptance confirms the browser-to-Twilio
        // leg only. The backend call log confirms when the patient answers.
        if (!this.wasConnectedInternal) this.emit({ phase: "ringing" });
      });
      call.on("reconnecting", () => this.emit({ phase: "reconnecting" }));
      call.on("reconnected", () =>
        this.emit({
          phase: this.wasConnectedInternal ? "connected" : "ringing",
        })
      );
      call.on("volume", (inputVolume, outputVolume) => {
        this.emit({
          volume: {
            input: Math.max(0, Math.min(1, inputVolume)),
            output: Math.max(0, Math.min(1, outputVolume)),
          },
        });
      });
      call.on("disconnect", () => {
        void this.mutateCallsSafely();
        this.disconnectFallback = setTimeout(() => {
          void this.finishCall("ended");
        }, DISCONNECT_FALLBACK_MS);
      });
      call.on("cancel", () => void this.finishCall("canceled"));
      call.on("reject", () => void this.finishCall("rejected"));
      call.on("error", (error) => {
        this.handleSdkError(error);
      });

      await this.mutateCallsSafely(options.deps);
      return true;
    } catch (error) {
      if (
        shouldAwaitBackendCallStatus({
          error,
          phase: this.snapshot.phase,
          wasConnected: this.wasConnectedInternal,
          activeCallLogId: this.snapshot.activeCallLogId,
        })
      ) {
        this.handleSdkError(error);
        return false;
      }
      const message = (error as Error).message || "Call failed.";
      this.emit({ errorMessage: message, phase: "error" });
      this.stopTimer();
      this.device?.destroy();
      this.device = null;
      this.activeCall = null;
      await this.mutateCallsSafely(options.deps);
      return false;
    }
  }

  async hangUp() {
    const call = this.activeCall;
    if (!call) return;
    call.disconnect();
    this.device?.disconnectAll();
    await this.finishCall("ended");
  }

  sendDigit(digit: string): boolean {
    if (!this.activeCall) return false;
    this.activeCall.sendDigits(digit);
    return true;
  }

  toggleMute() {
    if (!this.activeCall) return;
    const next = !this.snapshot.isMuted;
    this.activeCall.mute(next);
    this.emit({ isMuted: next });
  }

  /** Clears a terminal call state back to idle for the owning consumer. */
  reset(ownerKey: string) {
    if (ACTIVE_CALL_PHASES.includes(this.snapshot.phase)) return;
    const context = this.snapshot.context;
    if (context && context.ownerKey !== ownerKey) return;
    this.terminalHandled = false;
    this.wasConnectedInternal = false;
    this.emit({
      phase: "idle",
      elapsedSeconds: 0,
      volume: { input: 0, output: 0 },
      errorMessage: null,
      isMuted: false,
      wasConnected: false,
      activeCallLogId: null,
      context: null,
    });
  }
}

export const patientCallManager = new PatientCallManager();
